<template>
  <v-dialog v-model="dialog" max-width="560">
    <template v-slot:activator="{ on, attrs }">
      <v-btn text small color="primary" v-bind="attrs" v-on="on" class="mt-2">
        <v-icon left small>mdi-help-circle-outline</v-icon>วิธีการเข้าใช้งาน
      </v-btn>
    </template>

    <v-card rounded="lg" class="guide-dialog-card">
      <div class="guide-header">
        <v-icon size="32" color="white" class="mr-3">mdi-clipboard-list-outline</v-icon>
        <div>
          <h3 class="white--text mb-0">วิธีการเข้าใช้งานระบบ</h3>
          <p class="white--text caption mb-0 guide-header-sub">{{ config.groupLabel }}</p>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-card-text class="pt-6 pb-2">
        <div v-for="(step, i) in config.steps" :key="i" class="guide-step d-flex align-start mb-4">
          <v-avatar size="30" color="primary" class="white--text mr-3 flex-shrink-0">{{ i + 1 }}</v-avatar>
          <div>
            <strong>{{ step.title }}</strong>
            <p class="mb-0 grey--text text--darken-1 guide-step-desc">{{ step.desc }}</p>
          </div>
        </div>

        <v-divider class="my-4"></v-divider>

        <div class="guide-forgot-box">
          <div class="d-flex align-center mb-1">
            <v-icon small color="error" class="mr-2">mdi-lock-reset</v-icon>
            <strong>หากลืมรหัสผ่าน</strong>
          </div>
          <p class="mb-0 grey--text text--darken-1 guide-step-desc">
            กดปุ่ม "ลืมรหัสผ่าน" ใต้ฟอร์มเข้าสู่ระบบ ระบบจะให้ยืนยันตัวตนด้วย {{ config.identifierLabel }}
            ชื่อ-นามสกุล<span v-if="config.requireTel">, เบอร์โทรติดต่อ</span> และอีเมลที่ลงทะเบียนไว้ในระบบ
            จากนั้นระบบจะส่งรหัส OTP 6 หลักไปยังอีเมลนั้น เมื่อกรอก OTP ถูกต้องจึงจะตั้งรหัสผ่านใหม่ได้
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn color="primary" rounded @click="dialog = false">เข้าใจแล้ว</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * UsageGuideDialog.vue — คำอธิบายขั้นตอนการเข้าใช้งานระบบแบบละเอียด ต่อ 1 กลุ่มผู้ใช้งาน
 * ใช้ร่วมกันทั้ง 4 ฟอร์ม login ผ่าน prop personType (ค่าเดียวกับที่ ResetPasswordDialog ใช้อยู่แล้ว)
 * เนื้อหาแต่ละขั้นตอนอ้างอิงจากพฤติกรรมจริงของระบบ (login.php / thaid_helpers.php / ResetPasswordDialog.vue)
 * ไม่ใช่คำอธิบายทั่วไป — ถ้าพฤติกรรมระบบเปลี่ยน (เช่น เปิด ThaiID ให้กลุ่ม personnel_etc ในอนาคต)
 * ต้องปรับข้อความในนี้ตามให้ตรงด้วย
 */
const GUIDE_CONFIG = {
  // ข้าราชการครูและบุคลากรทางการศึกษา + พนักงานราชการ ใช้ตาราง personnel_temporary ร่วมกัน
  // (แยกกันด้วย user_status ตอน login เท่านั้น) ขั้นตอนจึงเหมือนกันทุกประการ
  personnel: {
    groupLabel: 'ข้าราชการครูและบุคลากรทางการศึกษา / พนักงานราชการ',
    identifierLabel: 'เลขบัตรประชาชน',
    requireTel: false,
    steps: [
      { title: 'กรอกชื่อผู้ใช้งาน', desc: 'ใช้เลขบัตรประชาชน 13 หลักของท่านเองเป็นชื่อผู้ใช้งาน' },
      { title: 'กรอกรหัสผ่าน', desc: 'หากเข้าใช้งานครั้งแรกหรือจำรหัสผ่านไม่ได้ กดปุ่ม "ลืมรหัสผ่าน" ใต้ฟอร์ม' },
      { title: 'กด "เข้าสู่ระบบ"', desc: 'เมื่อรหัสผ่านถูกต้อง ระบบจะพาไปยืนยันตัวตนผ่าน ThaiID ทันที (บังคับทุกบัญชี)' },
      { title: 'ยืนยันตัวตนผ่านแอป ThaiD', desc: 'สแกน QR Code ที่ปรากฏบนหน้าจอด้วยแอป ThaiD บนโทรศัพท์ แล้วยืนยันด้วย PIN ของแอป' },
      {
        title: 'เข้าสู่ระบบสำเร็จอัตโนมัติ',
        desc: 'เมื่อยืนยันตัวตนผ่าน ThaiD สำเร็จ ระบบจะพาท่านเข้าใช้งานทันที หากเป็นการเข้าใช้งานครั้งแรกหรือยังไม่เคยเปลี่ยนรหัสผ่าน ระบบจะให้ตั้งรหัสผ่านใหม่ก่อน'
      }
    ]
  },
  college: {
    groupLabel: 'สถานศึกษา / สถาบัน / สอจ. / ผู้ดูแลระบบ',
    identifierLabel: 'รหัสผู้ใช้งาน (รหัสสถานศึกษา/บัตรประชาชน)',
    requireTel: true,
    steps: [
      { title: 'กรอกชื่อผู้ใช้งาน', desc: 'ใช้รหัสหน่วยงานที่ได้รับจากส่วนกลาง (ใช้ร่วมกันทั้งหน่วยงาน ไม่ใช่บัญชีส่วนตัว)' },
      { title: 'กรอกรหัสผ่านของหน่วยงาน', desc: 'รหัสผ่านที่หน่วยงานใช้ร่วมกัน หากลืม กดปุ่ม "ลืมรหัสผ่าน" ใต้ฟอร์ม' },
      { title: 'กด "เข้าสู่ระบบ"', desc: 'ระบบจะพาไปยืนยันตัวตนผ่าน ThaiID ของ "ตัวท่านเอง" ไม่ใช่บัญชีของหน่วยงาน' },
      { title: 'ยืนยันตัวตนผ่านแอป ThaiD ด้วยบัญชีของตนเอง', desc: 'สแกน QR Code ด้วยแอป ThaiD บนโทรศัพท์ของท่านเอง แล้วยืนยันด้วย PIN ของแอป' },
      {
        title: 'ระบบตรวจสอบกับข้อมูลบุคลากรของหน่วยงาน',
        desc: 'หากเลขบัตรประชาชนที่ยืนยันมีข้อมูลอยู่ในระบบบุคลากรของหน่วยงานนี้ จะเข้าใช้งานได้ทันที เจ้าหน้าที่หลายคนในหน่วยงานเดียวกันใช้ ThaiID ของตนเองยืนยันแทนกันได้ ไม่จำกัดเฉพาะคนแรกที่เคยยืนยัน'
      },
      {
        title: 'หากยืนยันไม่ผ่าน',
        desc: 'ติดต่องานบุคลากรของหน่วยงานท่าน เพื่อตรวจสอบหรือเพิ่มข้อมูลของท่านในระบบก่อน จึงจะยืนยันตัวตนผ่าน ThaiID เข้าใช้งานได้'
      }
    ]
  },
  personnel_etc: {
    groupLabel: 'บุคลากรทางการศึกษาอื่น (ครูอัตราจ้าง / ลูกจ้างประจำ / ลูกจ้างชั่วคราว)',
    identifierLabel: 'เลขบัตรประชาชน / Passport / รหัสประจำตัว',
    requireTel: false,
    steps: [
      { title: 'กรอกชื่อผู้ใช้งาน', desc: 'ใช้เลขบัตรประชาชน / Passport / รหัสประจำตัวของท่านเองเป็นชื่อผู้ใช้งาน' },
      { title: 'กรอกรหัสผ่าน', desc: 'หากเข้าใช้งานครั้งแรกหรือจำรหัสผ่านไม่ได้ กดปุ่ม "ลืมรหัสผ่าน" ใต้ฟอร์ม' },
      { title: 'กด "เข้าสู่ระบบ"', desc: 'เมื่อรหัสผ่านถูกต้อง เข้าใช้งานได้ทันที กลุ่มนี้ยังไม่ต้องยืนยันตัวตนผ่าน ThaiID เพิ่มเติมในขณะนี้' },
      {
        title: 'ตั้งรหัสผ่านใหม่ (ถ้าจำเป็น)',
        desc: 'หากเป็นการเข้าใช้งานครั้งแรก หรือยังไม่เคยเปลี่ยนรหัสผ่าน ระบบจะให้ตั้งรหัสผ่านใหม่ก่อนใช้งานต่อ'
      }
    ]
  }
};

export default {
  name: 'UsageGuideDialog',
  props: {
    personType: {
      type: String,
      default: 'personnel',
      validator: v => Object.keys(GUIDE_CONFIG).includes(v)
    }
  },
  data() {
    return { dialog: false };
  },
  computed: {
    config() {
      return GUIDE_CONFIG[this.personType];
    }
  }
};
</script>

<style scoped>
.guide-header {
  background: linear-gradient(135deg, #455a64 0%, #455a64 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
}

.guide-header-sub {
  opacity: 0.85;
}

.guide-step-desc {
  font-size: 0.88rem;
  line-height: 1.6;
}

.guide-forgot-box {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 10px;
  padding: 12px 16px;
}
</style>
